import type { ADV } from "@/modules/adv2/interfaces/adv"
import type { MessageContent } from "@core/components/telegram/types"
import type {
  Binding,
  Bindings,
  ExternalId,
  NamedId,
  ReadBindings,
} from "../../interface/publications"
import {
  getActiveBindingSlots,
  isBinding,
  toBindingWrite,
} from "../../utils/bindings"
import { externalIdKey, isExternalId } from "../../utils/ids"
import { bindingCatalogs } from "./catalogs"

export const orderFilterKeys = ["agent_types", "agents", "campaigns"] as const
export type OrderFilters = Partial<
  Record<(typeof orderFilterKeys)[number], ExternalId[]>
>

/** Never spread page/global ADV filters into this optional PostBot workflow. */
export function buildOrderListRequest(
  filters: OrderFilters,
  pagination: { page: number; pageSize: number },
): ADV.OrdersListReq {
  const queryParams: ADV.OrdersListReq["queryParams"] = {
    page: pagination.page + 1,
    limit: pagination.pageSize,
  }
  for (const key of orderFilterKeys) {
    const ids = filters[key]
    if (ids?.length) {
      if (!ids.every(isExternalId)) throw new TypeError("Invalid filter ID")
      queryParams[key] = [...ids]
    }
  }
  return { queryParams }
}

export interface OrderLinkProposal {
  id: string
  source: "bot" | "url" | "invite" | "bot-url"
  label: string
  url: string
  value?: Binding
  reason?: string
}

type BindingCatalogs = ReturnType<typeof bindingCatalogs>

/** ADV already supplies usernames and REF separately. Only this recognized shape
 * may be turned into a URL; ordinary URLs keep their original fallback bytes. */
function telegramLink(link: ADV.Link) {
  let username = link.link.match(/^@?([A-Za-z][A-Za-z0-9_]{4,31})$/)?.[1]
  let start: string | null = null
  const fullUrl = isBinding({ url: link.link })
  if (!username) {
    if (!fullUrl)
      throw new Error("Некорректный HTTP(S) URL или Telegram username")
    const url = new URL(link.link)
    if (
      !["t.me", "telegram.me"].includes(url.hostname.toLowerCase()) ||
      url.username ||
      url.password ||
      url.port ||
      url.hash ||
      [...url.searchParams.keys()].some((key) => key !== "start") ||
      url.searchParams.getAll("start").length > 1
    )
      throw new Error(
        "URL не является однозначной ссылкой на Telegram-бота; используйте готовый URL",
      )
    username = decodeURIComponent(url.pathname).match(
      /^\/([A-Za-z][A-Za-z0-9_]{4,31})\/?$/,
    )?.[1]
    if (!username)
      throw new Error("В URL отсутствует корректный Telegram username")
    start = url.searchParams.get("start")
  }
  if (
    link.referral_code !== undefined &&
    typeof link.referral_code !== "string"
  )
    throw new Error("Некорректный REF")
  if (start !== null && link.referral_code && start !== link.referral_code)
    throw new Error("REF metadata не совпадает со start в фактическом URL")
  const referral = start ?? (link.referral_code || undefined)
  if (referral !== undefined && !/^[A-Za-z0-9_-]{1,64}$/.test(referral))
    throw new Error("Некорректный REF")
  const url = `https://t.me/${username}${referral ? `?start=${referral}` : ""}`
  return {
    username,
    referral,
    url,
    fallbackUrl: fullUrl && (start !== null || !referral) ? link.link : url,
  }
}

function resolveId(
  id: unknown,
  name: string,
  options: NamedId[],
  field: string,
  caseInsensitive = false,
): ExternalId {
  if (id !== undefined) {
    if (isExternalId(id)) return id
    throw new Error(`Некорректный ${field}`)
  }
  const matches = options.filter((option) =>
    caseInsensitive
      ? option.name.toLowerCase() === name.toLowerCase()
      : option.name === name,
  )
  const ids = new Set(matches.map((option) => externalIdKey(option.id)))
  if (ids.size !== 1)
    throw new Error(
      `Отсутствует корректный ${field}: нужно единственное совпадение в справочнике`,
    )
  return matches[0].id
}

function botValue(
  link: ADV.Link,
  parsed: ReturnType<typeof telegramLink>,
  catalogs: BindingCatalogs,
): Binding {
  const { username, referral } = parsed
  const value: Binding = {
    bot: {
      id: resolveId(link.bot_id, username, catalogs.bots, "bot_id", true),
      name: username,
    },
  }
  if (link.bot_type_id !== undefined || link.type?.trim()) {
    if (!link.type?.trim())
      throw new Error("Неполные metadata типа бота: нужны bot_type_id и имя")
    value.botType = {
      id: resolveId(
        link.bot_type_id,
        link.type,
        catalogs.botTypes,
        "bot_type_id",
      ),
      name: link.type,
    }
  }
  if (referral !== undefined || link.ref_id !== undefined) {
    if (!referral) throw new Error("Неполный REF: отсутствует значение")
    value.referral = {
      id: resolveId(link.ref_id, referral, catalogs.referrals, "ref_id"),
      name: referral,
    }
  }
  if (!isBinding(value)) throw new Error("Некорректные metadata бота или REF")
  return toBindingWrite(value)
}

export function orderLinkProposals(
  order: Pick<ADV.Order, "links" | "urls" | "invite_links">,
  catalogs: BindingCatalogs = bindingCatalogs(),
): OrderLinkProposal[] {
  const proposals: OrderLinkProposal[] = []
  const addUrl = (
    id: string,
    source: OrderLinkProposal["source"],
    label: string,
    url: string,
  ) => {
    const value = { url }
    proposals.push({
      id,
      source,
      label,
      url,
      ...(isBinding(value)
        ? { value }
        : { reason: "Некорректный HTTP(S) URL" }),
    })
  }
  for (const [index, link] of (order.links ?? []).entries()) {
    let fallbackUrl = link.link
    const proposal: OrderLinkProposal = {
      id: `bot:${index}`,
      source: "bot",
      label: `Бот ${index + 1}`,
      url: link.link,
    }
    try {
      const parsed = telegramLink(link)
      proposal.url = parsed.url
      fallbackUrl = parsed.fallbackUrl
      proposal.value = botValue(link, parsed, catalogs)
    } catch (error) {
      proposal.reason =
        error instanceof Error ? error.message : "Неполные metadata бота"
    }
    proposals.push(proposal)
    if (!proposal.value && isBinding({ url: fallbackUrl }))
      addUrl(
        `bot-url:${index}`,
        "bot-url",
        `Готовый URL бота ${index + 1} (без metadata)`,
        fallbackUrl,
      )
  }
  for (const [index, item] of (order.urls ?? []).entries())
    addUrl(`url:${index}`, "url", `URL ${index + 1}`, item.url)
  for (const [index, item] of (order.invite_links ?? []).entries())
    addUrl(
      `invite:${index}`,
      "invite",
      `Приглашение ${index + 1}`,
      item.invite_link,
    )
  return proposals
}

export interface OrderLinkAssignment {
  slotId: string
  proposalId: string
}

/** Only explicit choices may replace values. Revalidate current content/proposals at apply. */
export function buildOrderAssignments(
  content: MessageContent,
  _bindings: ReadBindings,
  proposals: readonly OrderLinkProposal[],
  assignments: readonly OrderLinkAssignment[],
): Bindings {
  const active = new Set(
    getActiveBindingSlots(content).map(({ slotId }) => slotId),
  )
  const replacements: Bindings = {}
  if (!assignments.length) throw new Error("Назначьте хотя бы одну ссылку")
  for (const { slotId, proposalId } of assignments) {
    if (
      !active.has(slotId) ||
      Object.prototype.hasOwnProperty.call(replacements, slotId)
    )
      throw new Error("Слот неактивен или назначен повторно")
    const proposal = proposals.find(({ id }) => id === proposalId)
    if (!proposal || proposal.reason || !isBinding(proposal.value))
      throw new Error("Предложение недоступно или содержит неполные metadata")
    replacements[slotId] = toBindingWrite(proposal.value)
  }
  return replacements
}
